/**
 * <s-cat-prompt> — the seasonal "You may like this" prompt (Halloween cat,
 * Guy Fawkes, Santa's elf). It renders hidden on product pages and only comes
 * in once the shopper has looked at a few different designs during their
 * visit (data-threshold, from Theme settings). It then swaps its card to a
 * design they haven't looked at yet, picked from the collection their viewed
 * designs share most (for example the same animal group), preferring the
 * garment they're viewing now. With no related design to suggest, it stays
 * hidden. The close button hides it for the rest of the visit.
 */
const DISMISSED_KEY = 'fyb-cat-prompt-dismissed';
const VIEWED_KEY = 'fyb-prompt-viewed';
const SINCE_KEY = 'fyb-prompt-since';
const MAX_VIEWED = 40;
const MAX_FETCHES = 3;

// Collections that group by audience, garment or "everything" say nothing about taste.
const GENERIC_COLLECTION = /^(shop-)?(mens|womens|unisex|kids)(-|$)|^(all|frontpage|new-arrivals|fleeces|best-sellers?|gift-cards?|seasonal-offer|start-here)$/;

/** A design is the handle without its audience and garment, e.g. herdwick-unisex-hoodie -> herdwick. */
function designOf(handle) {
  return String(handle || '').split(/-(?:unisex|womens|women|mens|menss|men|kids|kid)-/)[0];
}

function garmentOf(title) {
  const t = String(title || '').toLowerCase();
  if (/fleece|gilet/.test(t)) return 'fleece';
  if (/zip/.test(t)) return 'zip';
  if (/hood/.test(t)) return 'hoodie';
  if (/crew|sweat/.test(t)) return 'crew';
  if (/t[\s.=-]?shirt|tee/.test(t)) return 'tshirt';
  if (/mug/.test(t)) return 'mug';
  if (/beanie|cap|hat/.test(t)) return 'headwear';
  return '';
}

function readList(key) {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value : [];
  } catch (error) {
    return [];
  }
}

function writeList(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore blocked storage.
  }
}

function formatMoney(price, format) {
  const amount = Number.parseFloat(price);
  if (Number.isNaN(amount)) return '';
  const withSeparators = (value, decimals, thousands, decimal) => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  const template = format || '£{{amount}}';
  return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, kind) => {
    switch (kind) {
      case 'amount_no_decimals': return withSeparators(amount, 0, ',', '.');
      case 'amount_with_comma_separator': return withSeparators(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withSeparators(amount, 0, '.', ',');
      default: return withSeparators(amount, 2, ',', '.');
    }
  });
}

function sizedImage(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (error) {
    return src;
  }
}

class SCatPrompt extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();

    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (error) {
      // Storage can be blocked; the prompt behaves as normal.
    }

    // Product pages pass what is being viewed: record it before anything else.
    const trigger = this.dataset.viewedHandle ? this.#recordView() : null;

    if (dismissed) {
      this.hidden = true;
      return;
    }

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('[data-cat-prompt-close]')) return;
      this.hidden = true;
      try {
        sessionStorage.setItem(DISMISSED_KEY, '1');
      } catch (error) {
        // Ignore blocked storage.
      }
    }, { signal: this.#ac.signal });

    if (trigger) this.#maybeShow(trigger);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #recordView() {
    const handle = this.dataset.viewedHandle;
    const design = designOf(handle);
    let collections = [];
    try {
      collections = JSON.parse(this.dataset.viewedCollections || '[]')
        .filter((entry) => entry && entry.h && !GENERIC_COLLECTION.test(entry.h));
    } catch (error) {
      collections = [];
    }

    const viewed = readList(VIEWED_KEY).filter((entry) => entry.d !== design);
    viewed.push({ d: design, h: handle, c: collections });
    writeList(VIEWED_KEY, viewed.slice(-MAX_VIEWED));

    const since = readList(SINCE_KEY);
    if (!since.includes(design)) since.push(design);
    writeList(SINCE_KEY, since);

    return { design, handle, garment: garmentOf(this.dataset.viewedTitle), since, viewed };
  }

  async #maybeShow({ design, handle, garment, since, viewed }) {
    const threshold = Math.max(2, Number.parseInt(this.dataset.threshold || '3', 10) || 3);
    if (since.length < threshold) return;

    const suggestion = await this.#findSuggestion({ design, handle, garment, viewed });
    if (!suggestion || this.#ac.signal.aborted) return;

    this.#fillCard(suggestion);
    writeList(SINCE_KEY, []);
    this.hidden = false;
  }

  async #findSuggestion({ garment, viewed }) {
    const seenDesigns = new Set(viewed.map((entry) => entry.d));

    // Rank collections by how many viewed designs share them, then by how focused they are.
    const tally = new Map();
    for (const entry of viewed) {
      for (const collection of entry.c || []) {
        const current = tally.get(collection.h) || { handle: collection.h, size: collection.n || 9999, shared: 0 };
        current.shared += 1;
        tally.set(collection.h, current);
      }
    }
    const ranked = [...tally.values()].sort((a, b) => b.shared - a.shared || a.size - b.size);

    const base = (this.dataset.collectionsUrl || '').replace(/\/$/, '');
    if (!base) return null;

    for (const collection of ranked.slice(0, MAX_FETCHES)) {
      let products = [];
      try {
        const response = await fetch(`${base}/${encodeURIComponent(collection.handle)}/products.json?limit=60`, {
          signal: this.#ac.signal,
        });
        if (!response.ok) continue;
        products = (await response.json()).products || [];
      } catch (error) {
        if (this.#ac.signal.aborted) return null;
        continue;
      }

      const candidates = products.filter((product) => {
        if (!product || !product.handle || seenDesigns.has(designOf(product.handle))) return false;
        if (/gift[\s-]?card/i.test(`${product.product_type} ${product.handle}`)) return false;
        return (product.variants || []).some((variant) => variant.available);
      });
      if (candidates.length === 0) continue;

      const sameGarment = garment ? candidates.filter((product) => garmentOf(product.title) === garment) : [];
      const pool = sameGarment.length > 0 ? sameGarment : candidates;
      return pool[Math.floor(Math.random() * pool.length)];
    }

    return null;
  }

  #fillCard(product) {
    const productsBase = (this.dataset.productsUrl || '').replace(/\/$/, '');
    const url = `${productsBase}/${product.handle}`;
    const variant = (product.variants || []).find((entry) => entry.available) || (product.variants || [])[0];

    for (const link of this.querySelectorAll('a[href]')) link.href = url;

    const title = this.querySelector('[data-prompt-title]');
    if (title) title.textContent = product.title;

    const price = this.querySelector('[data-prompt-price]');
    if (price && variant) price.textContent = formatMoney(variant.price, this.dataset.moneyFormat);

    const image = this.querySelector('img');
    const src = product.images?.[0]?.src;
    if (image && src) {
      image.removeAttribute('srcset');
      image.src = sizedImage(src, 112);
      image.alt = product.title;
    }
  }
}

if (!customElements.get('s-cat-prompt')) {
  customElements.define('s-cat-prompt', SCatPrompt);
}
